import type { CompleteTaskFormValues } from "@/components/CompleteTaskForCaseButton.tsx";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import type { Document, DocumentData } from "@/types/document.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { handleJsonApiError } from "jsonapi-zod-query";

type SubmitData = {
    caseTaskUUID: string;
    documents: DocumentData[];
    formValues: CompleteTaskFormValues;
    saveType?: string;
};
export const useCompleteTaskWithDocuments = (): UseMutationResult<
    Document[],
    unknown,
    SubmitData
> => {
    const fetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (submitData: SubmitData) => {
            const response = await fetch(apiUrl("/document/complete-task"), {
                method: "POST",
                body: JSON.stringify({
                    ...submitData,
                    saveType: submitData.saveType ?? "complete",
                }),
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
            });

            await handleJsonApiError(response);
            return await response.json();
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: ["case-tasks"],
            });
            void queryClient.invalidateQueries({
                queryKey: ["case-notes"],
            });
            void queryClient.invalidateQueries({
                queryKey: ["case-media"],
            });
            void queryClient.invalidateQueries({
                queryKey: ["person-media"],
            });
            void queryClient.invalidateQueries({
                queryKey: ["company-media"],
            });
        },
    });
};
